// src/pages/employer/EmployerMembers.jsx
// Profile-level member management. Admins (org OWNER or profile ADMIN) can
// invite users by email, assign ADMIN/MEMBER role, change roles, remove
// members, resend pending invites, and copy the unique invite link to share
// manually until email sending is wired.

import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { useAuth } from '../../context/AuthContext';
import { orgAPI } from '../../services/api';
import { Modal, useConfirm } from '../../components/shared/UI';
import { Users, Plus, Trash2, Copy, Send, Check } from 'lucide-react';

const formatDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString(); } catch { return iso; }
};

const RoleBadge = ({ role }) => (
  <span style={{
    fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em',
    padding: '2px 8px', borderRadius: 10,
    background: role === 'ADMIN' ? '#0e4e95' : '#3D80CC', color: '#fff',
  }}>{role}</span>
);

const StatusBadge = ({ status }) => {
  const map = {
    ACCEPTED: { bg: '#28A745', label: 'Active' },
    PENDING:  { bg: '#E6A817', label: 'Pending' },
    EXPIRED:  { bg: '#999',    label: 'Expired' },
  };
  const s = map[status] || { bg: '#999', label: status };
  return (
    <span style={{
      fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em',
      padding: '2px 8px', borderRadius: 10, background: s.bg, color: '#fff',
    }}>{s.label}</span>
  );
};

const InviteModal = ({ open, onClose, onInvite, saving }) => {
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('MEMBER');
  useEffect(() => { if (open) { setEmail(''); setRole('MEMBER'); } }, [open]);
  const canSave = /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email.trim()) && !saving;
  return (
    <Modal open={open} onClose={onClose} title="Invite a member" width="460px"
      footer={
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button className="btn btn-secondary" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="btn btn-primary" disabled={!canSave}
            onClick={() => onInvite(email.trim().toLowerCase(), role)}>
            {saving ? 'Sending…' : 'Send invite'}
          </button>
        </div>
      }>
      <label className="form-label">Email address</label>
      <input className="form-input" type="email" value={email} autoFocus
        onChange={(e) => setEmail(e.target.value)}
        placeholder="user@example.com"
        onKeyDown={(e) => { if (e.key === 'Enter' && canSave) onInvite(email.trim().toLowerCase(), role); }} />

      <label className="form-label" style={{ marginTop: 12 }}>Permission</label>
      <select className="form-input" value={role} onChange={(e) => setRole(e.target.value)}>
        <option value="MEMBER">Member — post jobs, view candidates</option>
        <option value="ADMIN">Admin — full access incl. editing the profile + inviting others</option>
      </select>

      <p style={{ fontSize: 12, color: '#777', marginTop: 12, marginBottom: 0 }}>
        We'll create a unique invitation link. Until email delivery is wired, you'll
        be able to copy and share the link manually.
      </p>
    </Modal>
  );
};

const InviteLinkModal = ({ open, onClose, url, email, role }) => {
  const [copied, setCopied] = useState(false);
  useEffect(() => { if (open) setCopied(false); }, [open]);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast.success('Link copied');
    } catch {
      toast.error('Could not copy — select and copy manually');
    }
  };
  return (
    <Modal open={open} onClose={onClose} title="Invite link" width="540px"
      footer={
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <button className="btn btn-primary" onClick={onClose}>Done</button>
        </div>
      }>
      <p style={{ fontSize: 13, color: '#333', margin: '0 0 12px' }}>
        Share this link with <strong>{email}</strong> ({role}). It works whether
        they already have a Tikhet account or not — they'll be asked to log in
        or sign up before accepting.
      </p>
      <div style={{ display: 'flex', gap: 6 }}>
        <input className="form-input" value={url} readOnly
          style={{ flex: 1, fontFamily: 'monospace', fontSize: 12 }}
          onFocus={(e) => e.target.select()} />
        <button className="btn btn-primary" onClick={copy} style={{ flexShrink: 0 }}>
          {copied ? <><Check size={13} style={{ marginRight: 4 }} /> Copied</> : <><Copy size={13} style={{ marginRight: 4 }} /> Copy</>}
        </button>
      </div>
      <p style={{ fontSize: 11, color: '#888', marginTop: 10, marginBottom: 0 }}>
        The link expires in 14 days. If it's lost, use "Resend" on the member row to
        generate a fresh one.
      </p>
    </Modal>
  );
};

export const EmployerMembers = () => {
  const { activeOrg, activeProfile, user } = useAuth();
  const { confirm, dialog } = useConfirm();

  const [loading, setLoading]   = useState(true);
  const [members, setMembers]   = useState([]);
  const [inviting, setInviting] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [linkModal, setLinkModal] = useState(null); // { url, email, role }
  const [busyId, setBusyId] = useState(null);

  const reload = async () => {
    if (!activeOrg || !activeProfile) return;
    setLoading(true);
    try {
      const { data } = await orgAPI.getProfileMembers(activeOrg.id, activeProfile.id);
      setMembers(data?.data?.members || []);
    } catch {
      // interceptor
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [activeOrg?.id, activeProfile?.id]);

  const handleInvite = async (email, role) => {
    setInviting(true);
    try {
      const { data } = await orgAPI.inviteProfileMember(activeOrg.id, activeProfile.id, { email, role });
      const inviteUrl = data?.data?.inviteUrl;
      setInviteOpen(false);
      toast.success('Invite created');
      if (inviteUrl) setLinkModal({ url: inviteUrl, email, role });
      await reload();
    } catch {
      // interceptor
    } finally {
      setInviting(false);
    }
  };

  const handleResend = async (m) => {
    setBusyId(m.id);
    try {
      const { data } = await orgAPI.resendProfileInvite(activeOrg.id, activeProfile.id, m.id);
      const inviteUrl = data?.data?.inviteUrl;
      toast.success('Invite re-issued');
      if (inviteUrl) setLinkModal({ url: inviteUrl, email: m.email || m.user?.email, role: m.role });
      await reload();
    } catch {
      // interceptor
    } finally {
      setBusyId(null);
    }
  };

  const handleRemove = async (m) => {
    const label = m.user?.email || m.email || 'this member';
    const isPending = m.inviteStatus === 'PENDING';
    if (!await confirm(
      isPending ? 'Cancel invite' : 'Remove member',
      `${label} will lose access to this profile.`
    )) return;
    setBusyId(m.id);
    try {
      await orgAPI.removeProfileMember(activeOrg.id, activeProfile.id, m.id);
      toast.success(isPending ? 'Invite cancelled' : 'Member removed');
      await reload();
    } catch {
      // interceptor
    } finally {
      setBusyId(null);
    }
  };

  const handleRoleChange = async (m, role) => {
    if (role === m.role) return;
    setBusyId(m.id);
    try {
      await orgAPI.updateProfileMemberRole(activeOrg.id, activeProfile.id, m.id, { role });
      toast.success('Role updated');
      await reload();
    } catch {
      // interceptor
    } finally {
      setBusyId(null);
    }
  };

  const isMe = (m) => m.userId && user && m.userId === user.id;

  const decoratedMembers = members.map((m) => {
    const expired = m.inviteStatus === 'PENDING'
      && m.inviteExpiresAt && new Date(m.inviteExpiresAt) < new Date();
    return { ...m, inviteStatus: expired ? 'EXPIRED' : m.inviteStatus };
  });

  return (
    <div className="fade-in">
      <div className="page-header" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <div style={{ flex: 1 }}>
          <h1 style={{ margin: 0 }}>Members</h1>
          <p style={{ margin: '2px 0 0', color: 'var(--text-muted)', fontSize: 13 }}>
            Manage who can access {activeProfile?.profileName || 'this profile'}.
          </p>
        </div>
        <button className="btn btn-primary btn-sm" onClick={() => setInviteOpen(true)}>
          <Plus size={13} style={{ marginRight: 4 }} /> Invite
        </button>
      </div>

      {loading ? (
        <p style={{ color: '#888', padding: 20 }}>Loading…</p>
      ) : decoratedMembers.length === 0 ? (
        <div style={{
          background: 'white', border: '1px dashed var(--border)', borderRadius: 6,
          padding: 32, textAlign: 'center', color: '#888', marginTop: 16,
        }}>
          <Users size={28} style={{ marginBottom: 8, opacity: 0.6 }} />
          <div style={{ fontSize: 14, marginBottom: 8 }}>No members yet.</div>
          <button className="btn btn-primary btn-sm" onClick={() => setInviteOpen(true)}>
            <Plus size={13} style={{ marginRight: 4 }} /> Invite your first member
          </button>
        </div>
      ) : (
        <div style={{ background: 'white', border: '1px solid var(--border)', borderRadius: 6, overflow: 'hidden', marginTop: 16 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead style={{ background: '#F5F7FA' }}>
              <tr>
                {['User', 'Role', 'Status', 'Joined / invited', ''].map((h, i) => (
                  <th key={i} style={{
                    padding: '8px 14px', textAlign: 'left', color: '#0e4e95',
                    fontWeight: 700, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.06em',
                  }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {decoratedMembers.map((m) => {
                const email = m.user?.email || m.email || '—';
                const pending = m.inviteStatus === 'PENDING' || m.inviteStatus === 'EXPIRED';
                return (
                  <tr key={m.id} style={{ borderTop: '1px solid #eee' }}>
                    <td style={{ padding: '10px 14px', color: '#333' }}>
                      <div style={{ fontWeight: 600 }}>{email}</div>
                      {isMe(m) && <div style={{ fontSize: 11, color: '#3D80CC' }}>(you)</div>}
                    </td>
                    <td style={{ padding: '10px 14px' }}>
                      {pending ? (
                        <RoleBadge role={m.role} />
                      ) : (
                        <select value={m.role} disabled={busyId === m.id || isMe(m)}
                          onChange={(e) => handleRoleChange(m, e.target.value)}
                          style={{
                            fontSize: 12, fontWeight: 700, color: '#0e4e95',
                            padding: '4px 8px', borderRadius: 4, border: '1px solid #ccc',
                          }}>
                          <option value="MEMBER">MEMBER</option>
                          <option value="ADMIN">ADMIN</option>
                        </select>
                      )}
                    </td>
                    <td style={{ padding: '10px 14px' }}>
                      <StatusBadge status={m.inviteStatus} />
                    </td>
                    <td style={{ padding: '10px 14px', color: '#666' }}>
                      {m.acceptedAt ? formatDate(m.acceptedAt) : `Invited ${formatDate(m.invitedAt)}`}
                      {pending && m.inviteExpiresAt && (
                        <div style={{ fontSize: 11, color: '#999' }}>
                          {m.inviteStatus === 'EXPIRED' ? 'Expired' : 'Expires'} {formatDate(m.inviteExpiresAt)}
                        </div>
                      )}
                    </td>
                    <td style={{ padding: '8px 14px', textAlign: 'right' }}>
                      {pending && (
                        <button className="btn btn-outline btn-sm" style={{ marginRight: 6 }}
                          disabled={busyId === m.id} onClick={() => handleResend(m)} title="Re-issue invite link">
                          <Send size={12} style={{ marginRight: 4 }} /> Resend
                        </button>
                      )}
                      {!isMe(m) && (
                        <button className="btn btn-danger btn-sm"
                          disabled={busyId === m.id} onClick={() => handleRemove(m)}
                          title={pending ? 'Cancel invite' : 'Remove member'}>
                          <Trash2 size={12} />
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <InviteModal open={inviteOpen} onClose={() => setInviteOpen(false)}
        onInvite={handleInvite} saving={inviting} />
      <InviteLinkModal open={!!linkModal} onClose={() => setLinkModal(null)}
        url={linkModal?.url} email={linkModal?.email} role={linkModal?.role} />
      {dialog}
    </div>
  );
};
